// apps/admin/src/features/support/useSupportTicketRealtime.ts
//
// dbg-20260705-ticket-realtime-notify — global support-ticket realtime for
// admins. Mounted once near the app root (SupportTicketListener):
//   • polls stats (server-authoritative unread count) → sidebar Support badge
//   • subscribes ws `ticket.created` / `ticket.reply` → refetch + attention
//     (sound + desktop notification + tab-title flash)
//   • requests desktop-notification permission on first load
//
// All notification copy flows through the `support` i18n namespace — the
// browser Notification text is passed pre-localized into ticketNotify.

import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  useAuth,
  ensureNotificationPermission,
  showDesktopNotification,
  flashTitle,
  setTitleUnreadBadge,
} from '@printway/ui';

import { hasPermission } from '../../lib/rbac.js';
import { PERMISSIONS } from '../../lib/permissions.js';
import { wsClient } from '../../lib/wsClient.js';
import { supportTicketKeys } from '../../queries/supportTicketKeys.js';
import { getSupportTicketStats } from '../../api/support-tickets.api.js';
import { useNotificationStore } from '../../stores/useNotificationStore.js';
import { useTranslation } from '../../i18n/useTranslation.js';

interface TicketCreatedPayload {
  ticketId?: string;
  ticketNumber?: string | null;
  subject?: string;
  /** Side that authored the event — attention fires only for the other
   *  side's actions (staff must not chime itself — dbg-20260706-173837). */
  authorRole?: 'merchant' | 'staff';
}

export function useSupportTicketRealtime(): void {
  const user = useAuth((s) => s.user);
  const permissions = useAuth((s) => s.permissions);
  const canRead = hasPermission(
    user,
    permissions,
    PERMISSIONS.SUPPORT_TICKET_READ,
  );
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { t } = useTranslation('support');
  const setPendingCount = useNotificationStore((s) => s.setPendingCount);

  // Ask for desktop-notification permission once a support-capable admin loads.
  useEffect(() => {
    if (canRead) void ensureNotificationPermission();
  }, [canRead]);

  // Server-authoritative unread count → sidebar Support badge. Polled (≤30s)
  // as a fallback for the WS signal; also refetched on every ws event below.
  const statsQuery = useQuery({
    queryKey: [...supportTicketKeys.all(), 'stats'],
    queryFn: getSupportTicketStats,
    enabled: canRead,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  const unread = statsQuery.data?.unread ?? 0;
  useEffect(() => {
    if (!canRead) return;
    setPendingCount('support', unread);
    // Tab mirror — `(N) Printway Admin` title (same pattern as merchant-v3
    // useTabAlert) so the queue is visible from the tab strip. Favicon dot
    // dropped as redundant next to the title count (user feedback Image #13).
    setTitleUnreadBadge(unread);
  }, [canRead, unread, setPendingCount]);

  // WS realtime — new ticket / new public reply.
  useEffect(() => {
    if (!canRead) return undefined;
    const invalidate = (): void => {
      void qc.invalidateQueries({ queryKey: supportTicketKeys.all() });
    };

    const offCreated = wsClient.subscribe<TicketCreatedPayload>(
      'ticket.created',
      (frame) => {
        invalidate();
        // ws:admin echoes staff-authored events back — refetch, no alert.
        if (frame.payload?.authorRole === 'staff') return;
        const subject = frame.payload?.subject ?? '';
        // Sound dropped (dbg-20260710) — browser notification + tab flash only.
        flashTitle(t('realtime.tabFlash'));
        showDesktopNotification(t('realtime.newTicketTitle'), {
          body: subject || t('realtime.newTicketBody'),
          tag: `ticket-${frame.payload?.ticketId ?? 'new'}`,
          onClick: () => navigate('/support/tickets'),
        });
      },
    );

    const offReply = wsClient.subscribe<{
      ticketId?: string;
      authorRole?: 'merchant' | 'staff';
    }>('ticket.reply', (frame) => {
      invalidate();
      // Staff replying (this or another admin tab) must not alert the desk —
      // attention is for incoming merchant messages (dbg-20260706-173837).
      if (frame.payload?.authorRole === 'staff') return;
      // Sound dropped (dbg-20260710) — push a browser notification + tab flash.
      flashTitle(t('realtime.tabFlash'));
      showDesktopNotification(t('realtime.replyTitle'), {
        body: t('realtime.replyBody'),
        tag: `ticket-reply-${frame.payload?.ticketId ?? 'new'}`,
        onClick: () =>
          navigate(
            frame.payload?.ticketId
              ? `/support/tickets/${frame.payload.ticketId}`
              : '/support/tickets',
          ),
      });
    });

    return () => {
      offCreated();
      offReply();
    };
  }, [canRead, qc, navigate, t]);
}
